import { Subscription } from 'rxjs';
import { EwsRefresh } from '../../../../../services/ews-refresh';
import { OnDestroy, OnChanges, SimpleChanges, Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { lastValueFrom, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import {
  EwsDataService, BeanLogicResult, CustomerInputRow, ForecastLine, BurnRange, dateOnly, monthLabel
} from '../../../../../services/ews-data.service';
import { downloadCsv } from '../../../../../services/swap-data.service';
import { ReportService } from '../../../../../services/report.service';
import { PurchaseProposalData } from '../../../../../reports/purchase-proposal/purchase-proposal.component';

// =====================================================================
// EwsBeanLogicComponent — Scope 6.4 Bean Logic (S4, legacy "Virtual
// Inventory Manager") — one customer at a time
// ---------------------------------------------------------------------
// Per coffee group: burn / month (trader can override), bags on reserve,
// bags at the customer's roastery (input), new-crop date, bags needed to
// get to new crop, the RECOMMENDATION, RNY bags available, a 12/24-month
// surplus / deficit bar with the new-crop month marked, est. depletion.
// Edits are previewed live (same formula as the proc) and saved together
// with "Save changes"; a row put back to defaults is removed on save.
// "Save snapshot" stores the recommendations as a BEAN_LOGIC forecast run
// (shows up in EWS Saved Forecasts).
// "Purchase Proposal PDF" = customer copy of the rows ticked Print
// (ReportService → reports/purchase-proposal) and, when something is
// recommended, a PROPOSAL run with the bags per group.
// Data: GET /api/v1/ews/bean-logic/customers · /bean-logic/:CompanyID
//       POST /bean-logic/inputs/save · /runs/save
// =====================================================================

interface BarCell {
  label: string;
  value: number;
  arriving: number;
  crop: boolean;
}

interface BeanRow {
  src: any;                       // RS2 row as loaded
  GroupID: number;
  Name: string;
  Section: string;
  // inputs (what the trader is editing)
  burnOverride: number | null;
  roastery: number | null;
  print: boolean;
  accepted: boolean;
  // live results
  BurnUsed: number;
  Needed: number;
  Recommended: number;
  Short: boolean;
  RecText: string;
  Bars: BarCell[];
  DepletionMonth: string | null;
  MonthsCovered: number | null;
  // detail
  Skus: any[];
  ByWarehouse: any[];
  expanded: boolean;
}

@Component({
  selector: 'app-ews-bean-logic',
  imports: [RmsModule],
  templateUrl: './bean-logic.component.html',
  styleUrl: './bean-logic.component.scss'
})
export class EwsBeanLogicComponent implements OnInit, OnDestroy {
  private refresh = new EwsRefresh();
  private liveSubscription?: Subscription;
  UpdatedAt: Date | null = null;
  LivePaused = false;
  CustomerNotes: any[] = [];
  NoteText = '';
  CustomerVisible = false;
  SavingNote = false;
  queueRefresh() {
    this.refresh.schedule(() => {
      if (this.DirtyCount || this.Saving || this.NoteText.trim()) { this.LivePaused = true; this.cdr.markForCheck(); return; }
      if (this.CompanyID) this.load();
    });
  }
  ngOnDestroy() { this.refresh.destroy(); this.liveSubscription?.unsubscribe(); }

  Loading = false;
  Saving = false;
  Generating = false;

  // ---- filter bar ----
  CustomerOptions: { value: number; label: string }[] = [];
  private Customers: any[] = [];
  CompanyID: number | null = null;
  BurnRange: BurnRange = 3;
  BurnRangeOptions = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' }
  ];
  WarehouseID: any = '';                       // '' = the customer's usual warehouse
  WarehouseOptions: { value: any; label: string }[] = [{ value: '', label: "Customer's usual" }];
  private WarehouseNames = new Map<number, string>();
  BarMonths = 12;
  BarMonthOptions = [
    { value: 12, label: '12 months' },
    { value: 24, label: '24 months' }
  ];
  ShowAll = false;

  // ---- results ----
  Data: BeanLogicResult | null = null;
  Header: any = null;
  Rows: BeanRow[] = [];
  private RowsCompanyID: number | null = null;
  Sections: { name: string; rows: BeanRow[] }[] = [];

  constructor(
    private ews: EwsDataService,
    private msg: RmsMessageService,
    private reports: ReportService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await Promise.all([this.loadCustomers(), this.loadWarehouses()]);
    this.liveSubscription = this.ews.changes$.subscribe(() => this.queueRefresh());
  }

  // ---------------------------------------------------------------------
  // Pickers
  // ---------------------------------------------------------------------

  async loadCustomers() {
    try {
      this.Customers = await lastValueFrom(this.ews.getBeanCustomers(null)) ?? [];
      this.CustomerOptions = this.Customers.map(c => ({
        value: c.CompanyID,
        label: `${c.CompanyName} (#${c.CompanyID})${c.OnReserve ? ` · ${fmt(c.OnReserve)} on reserve` : ''}`
      }));
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the customers.');
    } finally {
      this.cdr.markForCheck();
    }
  }

  async loadWarehouses() {
    try {
      const opt = await lastValueFrom(this.ews.getOptions());
      for (const w of opt?.Warehouses ?? []) this.WarehouseNames.set(w.WarehouseID, w.LocationShortName || w.LocationName);
      this.WarehouseOptions = [
        { value: '', label: "Customer's usual" },
        ...(opt?.Warehouses ?? []).map((w: any) => ({ value: w.WarehouseID, label: w.LocationShortName || w.LocationName }))
      ];
      const burn = Number((opt?.Settings ?? []).find((s: any) => s.SettingKey === 'DefaultBurnRange')?.SettingValue);
      if ([3, 6, 12].includes(burn)) this.BurnRange = burn as BurnRange;
    } catch {
      // the filter bar still works with the defaults
    }
  }

  /** rows with unsaved burn / roastery numbers (Print / Accepted ticks don't change any number) */
  get NumbersDirtyCount(): number {
    return this.Rows.reduce((n,r)=>n+(this.isNumbersDirty(r)?1:0)+r.Skus.filter(s=>this.skuDirty(s)).length,0);
  }

  /** the picker values the rows on screen were loaded with */
  private LastRunKey: string | null = null;

  private filterKey(): string {
    return JSON.stringify([this.CompanyID ?? null, this.BurnRange, String(this.WarehouseID ?? ''), this.BarMonths]);
  }

  /** customer / burn / warehouse / bar changed after the last Run */
  get FiltersChanged(): boolean {
    if (this.Loading || !this.CompanyID) return false;
    return this.LastRunKey === null ? true                  // customer picked, not loaded yet
      : this.filterKey() !== this.LastRunKey;
  }

  get DirtyCount(): number {
    return this.Rows.reduce((n,r) => n + (this.isDirty(r) ? 1 : 0) + r.Skus.filter(s=>this.skuDirty(s)).length,0);
  }

  // ---------------------------------------------------------------------
  // Load one customer
  // ---------------------------------------------------------------------

  async load() {
    if (!this.CompanyID) { this.msg.warn('Pick a customer.'); return; }
    if (this.NoteText.trim() && this.Header?.CompanyID !== this.CompanyID) {
      if (!confirm('Discard the unsaved note and switch customer?')) return;
      this.NoteText = '';
    }
    if (this.DirtyCount && !confirm(`You have ${this.DirtyCount} unsaved change(s). Load anyway and lose them?`)) return;
    const revision = this.refresh.begin();
    const runKey = this.filterKey();

    this.Loading = true;
    this.cdr.markForCheck();
    try {
      const d = await lastValueFrom(this.ews.getBeanLogic(this.CompanyID, {
        BurnRange: this.BurnRange,
        WarehouseID: this.WarehouseID === '' ? null : Number(this.WarehouseID),
        BarMonths: this.BarMonths
      }));
      if (!this.refresh.current(revision)) return;
      this.Data = d;
      this.Header = d?.Header ?? null;
      this.buildRows();
      this.LastRunKey = runKey;
      this.LivePaused = false;
      this.UpdatedAt = new Date();
      this.loadNotes();
    } catch (err: any) {
      if (this.refresh.current(revision)) this.msg.error(err?.error?.error ?? 'Could not load Bean Logic for this customer.');
    } finally {
      if (!this.refresh.current(revision)) return;
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  private buildRows() {
    const d = this.Data;
    const previous = new Map((this.RowsCompanyID === this.Header?.CompanyID ? this.Rows : []).map(r=>[r.GroupID,r]));
    this.RowsCompanyID = this.Header?.CompanyID ?? null;
    const bars = d?.Bars ?? [];
    const skus = d?.Skus ?? [];
    const byWh = d?.RnyByWarehouse ?? [];

    this.Rows = (d?.Groups ?? []).map((g: any) => {
      const row: BeanRow = {
        src: g,
        GroupID: g.GroupID,
        Name: g.ParentGroupName ? `${g.ParentGroupName} › ${g.GroupName}` : g.GroupName,
        Section: g.Section || 'Other',
        burnOverride: g.BurnOverride === null || g.BurnOverride === undefined ? null : Number(g.BurnOverride),
        roastery: g.RoasteryBags ? Number(g.RoasteryBags) : null,
        print: g.ShowInPrint !== false && g.ShowInPrint !== 0,
        accepted: !!g.CustomerAccepted,
        BurnUsed: 0, Needed: 0, Recommended: 0, Short: false, RecText: '',
        Bars: [], DepletionMonth: null, MonthsCovered: null,
        Skus: skus.filter((s: any) => s.GroupID === g.GroupID).map((s: any) => ({
          ...s, burnOverride: s.BurnOverride ?? null, roastery: s.RoasteryBags ?? null,
          ContractThru: dateOnly(s.ContractThru), NextUnsoldArrival: dateOnly(s.NextUnsoldArrival), LastPickup: dateOnly(s.LastPickup)
        })),
        ByWarehouse: byWh.filter((w: any) => w.GroupID === g.GroupID).map((w: any) => ({
          ...w,
          WarehouseName: w.WarehouseID === -1 ? 'Other locations' : (this.WarehouseNames.get(w.WarehouseID) ?? `Warehouse ${w.WarehouseID}`),
          NextUnsoldArrival: dateOnly(w.NextUnsoldArrival)
        })),
        expanded: previous.get(g.GroupID)?.expanded ?? false
      };
      (row as any)._bars = bars.filter((b: any) => b.GroupID === g.GroupID).sort((a: any, b: any) => a.MonthIndex - b.MonthIndex);
      const target = (previous.get(g.GroupID) as any)?.coverThrough;
      if (target) {
        (row as any).coverThrough = target;
        (row as any).coverMonths = Math.max(0,Math.round((Date.parse(target+'T00:00:00Z')-Date.parse(dateOnly(this.Header.ForecastDate)!+'T00:00:00Z'))/86400000/30.4375*100)/100);
      }
      this.recalc(row);
      return row;
    });
    this.buildSections();
  }

  buildSections() {
    const order = ['Customer Groupings', 'Conventional', 'Organic', 'Decaf', 'Robusta'];
    const visible = this.Rows.filter(r => this.ShowAll || !r.src.IsHidden);
    const names = [...new Set(visible.map(r => r.Section))]
      .sort((a, b) => (order.indexOf(a) < 0 ? 99 : order.indexOf(a)) - (order.indexOf(b) < 0 ? 99 : order.indexOf(b)));
    this.Sections = names.map(n => ({ name: n, rows: visible.filter(r => r.Section === n) }));
  }

  // ---------------------------------------------------------------------
  // Live preview — the same formula as ews_get_bean_logic:
  //   BurnUsed    = override, else the customer's burn for the range
  //   Needed      = CEILING(BurnUsed × MonthsToCover)   (to end of crop month)
  //   Recommended = MAX(0, Needed − OnReserve − RoasteryBags)
  //   bar closing = ReservedSpot + Roastery + reserve arrivals so far − BurnUsed × month
  // ---------------------------------------------------------------------

  recalc(r: BeanRow) {
    const g = r.src;
    const burn = r.burnOverride !== null && r.burnOverride !== undefined && !Number.isNaN(r.burnOverride)
      ? Math.max(0, Number(r.burnOverride)) : Math.max(0, (Number(g.BurnDefault) || 0) + this.skuBurnAdjustment(r));
    const roastery = Math.max(0, Number(r.roastery) || 0) + r.Skus.reduce((n,s) => n+Math.max(0,Number(s.roastery)||0),0);
    const months = (r as any).coverMonths ?? (Number(g.MonthsToCover) || 0);
    const onReserve = Number(g.OnReserve) || 0;

    r.BurnUsed = Math.round(burn * 100) / 100;
    r.Needed = burn > 0 ? Math.ceil(Math.round(burn * months * 1e6) / 1e6) : 0;
    r.Recommended = Math.max(0, r.Needed - onReserve - roastery);
    const available = (Number(g.RnySpotUnsold) || 0) + (Number(g.RnyForwardUnsold) || 0);
    r.Short = r.Recommended > 0 && available < r.Recommended;

    const cover = shortMonth((r as any).coverThrough ?? g.CoverThrough);
    r.RecText = r.Recommended > 0
      ? `Recommend booking ${fmt(r.Recommended)} ${r.Recommended === 1 ? 'bag' : 'bags'} through end of ${cover} – New Crop.`
      : burn > 0 ? `Covered through end of ${cover} – New Crop.` : '';

    // bars
    const src: any[] = (r as any)._bars ?? [];
    let cum = 0;
    const start = (Number(g.ReservedSpot) || 0) + roastery;
    r.Bars = src.map((b: any, i: number) => {
      cum += Number(b.ReserveArriving) || 0;
      return {
        label: monthLabel(b.MonthStart),
        value: Math.round(start + cum - burn * (i + 1)),
        arriving: Math.round(Number(b.ReserveArriving) || 0),
        crop: !!b.IsCropMonth
      };
    });

    // depletion of the customer's own cover (first month below zero)
    const firstNeg = r.Bars.findIndex(b => b.value < 0);
    if (burn <= 0) { r.DepletionMonth = null; r.MonthsCovered = null; }
    else if (firstNeg < 0) { r.DepletionMonth = null; r.MonthsCovered = r.Bars.length; }
    else {
      const bn = r.Bars[firstNeg];
      const part = Math.min(1, Math.max(0, (bn.value + burn) / burn));
      r.DepletionMonth = src[firstNeg]?.MonthStart ? dateOnly(src[firstNeg].MonthStart) : null;
      r.MonthsCovered = Math.round((firstNeg + part) * 10) / 10;
    }
  }

  onEdit(r: BeanRow) {
    if (r.burnOverride !== null && (r.burnOverride as any) === '') r.burnOverride = null;
    if (r.roastery !== null && (r.roastery as any) === '') r.roastery = null;
    this.recalc(r);
  }

  private skuBurnAdjustment(r: BeanRow): number {
    return r.Skus.reduce((n,s) => n + (s.burnOverride == null || s.burnOverride === '' ? 0 : Math.max(0,Number(s.burnOverride)||0) - Number(s.BurnDefault || 0)), 0);
  }
  skuDirty(s: any): boolean {
    return normBurn(s.burnOverride) !== normBurn(s.BurnOverride) || normBags(s.roastery) !== normBags(s.RoasteryBags);
  }
  chooseCoverage(r: BeanRow, index: number) {
    const source = (r as any)._bars[index];
    if (!source) return;
    const date = new Date(dateOnly(source.MonthStart)! + 'T00:00:00Z');
    date.setUTCMonth(date.getUTCMonth()+1,0);
    (r as any).coverThrough = date.toISOString().substring(0,10);
    const today = new Date(dateOnly(this.Header.ForecastDate)! + 'T00:00:00Z');
    (r as any).coverMonths = Math.max(0, Math.round((date.getTime()-today.getTime())/86400000/30.4375*100)/100);
    this.recalc(r);
  }
  async loadNotes() {
    const company = this.Header?.CompanyID;
    if (!company) return;
    try {
      const notes = await lastValueFrom(this.ews.getNotes({NoteScope:'BEAN_LOGIC', CompanyID:company}));
      if (this.Header?.CompanyID === company) this.CustomerNotes = notes ?? [];
      this.cdr.markForCheck();
    } catch { this.msg.error('Could not load customer notes.'); }
  }
  async addCustomerNote() {
    if (!this.NoteText.trim() || !this.Header || this.SavingNote) return;
    this.SavingNote = true;
    try {
      await lastValueFrom(this.ews.saveNote({NoteScope:'BEAN_LOGIC',CompanyID:this.Header.CompanyID,NoteText:this.NoteText.trim(),IsCustomerVisible:this.CustomerVisible,NoteType:'General'}));
      this.NoteText = ''; await this.loadNotes();
    } catch (e: any) { this.msg.error(e?.error?.error ?? 'Could not save the note.'); }
    finally { this.SavingNote = false; this.cdr.markForCheck(); }
  }
  emailDraft() {
    if (!this.Header) return;
    const body = this.VisibleRows.filter(r=>r.print).map(r=>`${r.Name}: ${r.RecText}`).join('\n');
    window.location.href = `mailto:?subject=${encodeURIComponent('Coffee inventory review — '+this.Header.CompanyName)}&body=${encodeURIComponent(body)}`;
  }

  /** legacy "Reset to Default": no override, nothing at the roastery, shown in print, not accepted */
  resetRow(r: BeanRow) {
    r.burnOverride = null;
    r.roastery = null;
    r.print = true;
    r.accepted = false;
    this.recalc(r);
  }

  /** legacy "Clear Changes": back to what was loaded */
  clearChanges() {
    for (const r of this.Rows) {
      r.burnOverride = r.src.BurnOverride === null || r.src.BurnOverride === undefined ? null : Number(r.src.BurnOverride);
      r.roastery = r.src.RoasteryBags ? Number(r.src.RoasteryBags) : null;
      r.print = r.src.ShowInPrint !== false && r.src.ShowInPrint !== 0;
      r.accepted = !!r.src.CustomerAccepted;
      for (const s of r.Skus) { s.burnOverride = s.BurnOverride ?? null; s.roastery = s.RoasteryBags ?? null; }
      this.recalc(r);
    }
    this.LivePaused = false;
    this.queueRefresh();
  }

  isNumbersDirty(r: BeanRow): boolean {
    const s = r.src;
    const o = s.BurnOverride === null || s.BurnOverride === undefined ? null : Number(s.BurnOverride);
    const ro = s.RoasteryBags ? Number(s.RoasteryBags) : null;
    return normBurn(r.burnOverride) !== normBurn(o) || normBags(r.roastery) !== normBags(ro);
  }

  // ---------------------------------------------------------------------
  // Print — Select all / Unselect all (then tick just the ones you want)
  // ---------------------------------------------------------------------

  /** rows on screen right now (hidden groups only when "show all" is ticked) */
  private get VisibleRows(): BeanRow[] {
    return this.Rows.filter(r => this.ShowAll || !r.src.IsHidden);
  }

  get AllPrinted(): boolean {
    const v = this.VisibleRows;
    return v.length > 0 && v.every(r => r.print);
  }

  get PrintCount(): number {
    return this.VisibleRows.filter(r => r.print).length;
  }

  togglePrintAll() {
    const tick = !this.AllPrinted;            // all ticked → untick all, otherwise tick all
    for (const r of this.VisibleRows) r.print = tick;
  }

  isDirty(r: BeanRow): boolean {
    const s = r.src;
    const o = s.BurnOverride === null || s.BurnOverride === undefined ? null : Number(s.BurnOverride);
    const ro = s.RoasteryBags ? Number(s.RoasteryBags) : null;
    const p = s.ShowInPrint !== false && s.ShowInPrint !== 0;
    return normBurn(r.burnOverride) !== normBurn(o) || normBags(r.roastery) !== normBags(ro)
      || r.print !== p || r.accepted !== !!s.CustomerAccepted;
  }

  // ---------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------

  async saveChanges() {
    if (!this.Header) return;
    const dirty = this.Rows.filter(r => this.isDirty(r));
    if (!this.DirtyCount) { this.msg.info('Nothing changed.'); return; }

    const rows: CustomerInputRow[] = dirty.map(r => ({
      GroupID: r.GroupID,
      InventoryTypeID: null,
      RoasteryBags: r.roastery ? Math.round(Number(r.roastery)) : null,
      BurnRateOverride: r.burnOverride === null || r.burnOverride === undefined ? null : Math.round(Number(r.burnOverride) * 100) / 100,
      ShowInPrint: r.print,
      CustomerAccepted: r.accepted
    }));

    for (const r of this.Rows) for (const s of r.Skus.filter(s=>this.skuDirty(s))) rows.push({
      GroupID:r.GroupID, InventoryTypeID:s.InventoryTypeID,
      BurnRateOverride:s.burnOverride == null || s.burnOverride === '' ? null : Number(s.burnOverride),
      RoasteryBags:s.roastery ? Math.round(Number(s.roastery)) : null,
      ShowInPrint:s.ShowInPrint !== false, CustomerAccepted:!!s.CustomerAccepted
    });
    this.Saving = true;
    try {
      const res = await lastValueFrom(this.ews.saveCustomerInputs(this.Header.CompanyID, rows));
      this.msg.success(`Saved ${res?.RowsChanged ?? rows.length} change(s).`);
      // reload so the numbers come from the database
      const d = await lastValueFrom(this.ews.getBeanLogic(this.Header.CompanyID, {
        BurnRange: this.BurnRange,
        WarehouseID: this.WarehouseID === '' ? null : Number(this.WarehouseID),
        BarMonths: this.BarMonths
      }));
      this.Data = d;
      this.Header = d?.Header ?? null;
      this.buildRows();
      this.LastRunKey = this.filterKey();
      this.LivePaused = false;
      this.UpdatedAt = new Date();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save the changes.');
    } finally {
      this.Saving = false;
      this.cdr.markForCheck();
    }
  }

  /** store today's recommendations as a BEAN_LOGIC run (EWS Saved Forecasts) */
  async saveSnapshot() {
    if (!this.Header) return;
    if (this.NumbersDirtyCount) { this.msg.warn('Save your burn / roastery changes first, so the snapshot matches the database.'); return; }
    const name = prompt('Name for this snapshot:', `${this.Header.CompanyName} — Bean Logic ${new Date().toISOString().substring(0, 10)}`);
    if (name === null) return;

    const shown = this.Rows.filter(r => !r.src.IsHidden);
    const lines: ForecastLine[] = shown.map(r => ({
      LineType: 'RECOMMENDATION',
      GroupID: r.GroupID,
      WarehouseID: this.Header.WarehouseID,
      BurnRate: r.BurnUsed,
      OpeningBags: Number(r.src.ReservedSpot) || 0,
      ReservedBags: Number(r.src.OnReserve) || 0,
      RecommendedBags: r.Recommended,
      LineNote: (r.RecText + (r.Short ? ' Not enough inventory — contact your trader.' : '')).substring(0, 500)
    }));

    this.Saving = true;
    try {
      const res = await lastValueFrom(this.ews.saveRun({
        RunType: 'BEAN_LOGIC',
        RunName: name.trim() || null,
        AsOfDate: dateOnly(this.Header.BurnWindowEnd),
        BurnRange: this.Header.BurnRange,
        WarehouseID: this.Header.WarehouseID,
        CompanyID: this.Header.CompanyID,
        GroupIDs: shown.map(r => r.GroupID),
        Assumptions: {
          CoverageTargets: shown.map(r=>({GroupID:r.GroupID,CoverThrough:this.coverThrough(r)})),
          ForecastDate: dateOnly(this.Header.ForecastDate),
          IsTestMode: !!this.Header.IsTestMode,
          WarehouseName: this.Header.WarehouseName,
          TotalOnReserve: this.Header.TotalOnReserve,
          TotalRecommended: this.TotalRecommended
        },
        Lines: lines
      }));
      this.msg.success(`Snapshot saved as version ${res.VersionNo}.`);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save the snapshot.');
    } finally {
      this.Saving = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------
  // Purchase Proposal PDF (customer copy)
  // ---------------------------------------------------------------------

  async makeProposal() {
    if (!this.Header) return;
    // Print ticks are used as they are on screen (no need to save them first);
    // burn / roastery numbers must be saved so the proposal matches the database
    if (this.NumbersDirtyCount) { this.msg.warn('Save your burn / roastery changes first, so the proposal matches the database.'); return; }

    const rows = this.Rows.filter(r => !r.src.IsHidden && r.print);
    if (!rows.length) { this.msg.warn('No groups are ticked for Print.'); return; }

    const h = this.Header;
    const data: PurchaseProposalData = {
      CompanyID: h.CompanyID,
      CompanyName: h.CompanyName,
      WarehouseName: h.WarehouseName ?? '',
      BurnRange: h.BurnRange,
      BurnWindowStart: dateOnly(h.BurnWindowStart),
      BurnWindowEnd: dateOnly(h.BurnWindowEnd),
      PreparedDate: dateOnly(h.ForecastDate) ?? new Date().toISOString().substring(0, 10),
      IsTestMode: !!h.IsTestMode,
      Rows: rows.map(r => ({
        Section: r.Section,
        Name: r.Name,
        BurnUsed: r.BurnUsed,
        OnReserve: Number(r.src.OnReserve) || 0,
        ReservedSpot: Number(r.src.ReservedSpot) || 0,
        ReservedForward: Number(r.src.ReservedForward) || 0,
        ContractThru: dateOnly(r.src.ContractThru),
        Roastery: this.totalRoastery(r),
        NewCrop: dateOnly(r.src.Crop1Arrival),
        CoverThrough: this.coverThrough(r),
        Needed: r.Needed,
        Recommended: r.Recommended,
        Available: (Number(r.src.RnySpotUnsold) || 0) + (Number(r.src.RnyForwardUnsold) || 0),
        Short: r.Short,
        RecText: r.RecText,
        DepletionLabel: r.DepletionMonth ? monthLabel(r.DepletionMonth)
          : r.BurnUsed > 0 ? `beyond ${r.Bars.length} mo` : '—',
        Bars: r.Bars.map(b => ({ label: b.label, value: b.value, crop: b.crop }))
      }))
    };

    this.Generating = true;
    this.cdr.markForCheck();
    try {
      // 1 · the PDF (same ReportService as Delivery Order / Payment Receipt)
      const dataUrl: string = await this.reports.generatePDF('PurchaseProposal', data);
      const file = `Purchase Proposal - ${safeFileName(h.CompanyName)} - ${data.PreparedDate}.pdf`;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = file;
      a.click();

      // 2 · history: a PROPOSAL run when something is recommended
      const toBook = data.Rows.filter(r => r.Recommended > 0);
      if (toBook.length) {
        const lines: ForecastLine[] = rows.filter(r => r.Recommended > 0).map(r => ({
          LineType: 'PROPOSAL',
          GroupID: r.GroupID,
          WarehouseID: h.WarehouseID,
          BurnRate: r.BurnUsed,
          ReservedBags: Number(r.src.OnReserve) || 0,
          RecommendedBags: r.Recommended,
          ProposedBags: r.Recommended,
          LineNote: r.RecText.substring(0, 500)
        }));
        const res = await lastValueFrom(this.ews.saveRun({
          RunType: 'PROPOSAL',
          RunName: `Purchase Proposal — ${h.CompanyName} ${data.PreparedDate}`.substring(0, 200),
          AsOfDate: data.BurnWindowEnd,
          BurnRange: h.BurnRange,
          WarehouseID: h.WarehouseID,
          CompanyID: h.CompanyID,
          GroupIDs: rows.map(r => r.GroupID),
          Assumptions: {
            ForecastDate: data.PreparedDate,
            IsTestMode: data.IsTestMode,
            WarehouseName: data.WarehouseName,
            GroupsPrinted: rows.length,
            TotalProposed: toBook.reduce((t, r) => t + r.Recommended, 0),
            FileName: file
          },
          Lines: lines
        }));
        this.msg.success(`Purchase proposal downloaded and saved (version ${res.VersionNo}).`);
      } else {
        this.msg.success('Purchase proposal downloaded — everything is covered, so nothing was saved as a proposal.');
      }
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not make the purchase proposal.');
    } finally {
      this.Generating = false;
      this.cdr.markForCheck();
    }
  }

  exportCsv() {
    if (!this.Header) return;
    const rows = this.Rows.filter(r => this.ShowAll || !r.src.IsHidden).map(r => ({
      Section: r.Section,
      Group: r.Name,
      Burn3: r.src.Burn3, Burn6: r.src.Burn6, Burn12: r.src.Burn12,
      BurnDefault: r.src.BurnDefault, BurnOverride: r.burnOverride, BurnUsed: r.BurnUsed,
      ReservedSpot: r.src.ReservedSpot, ReservedForward: r.src.ReservedForward, OnReserve: r.src.OnReserve,
      RoasteryBags: this.totalRoastery(r),
      NewCrop: dateOnly(r.src.Crop1Arrival), CoverThrough: this.coverThrough(r),
      BagsNeededToCrop: r.Needed, RecommendedBags: r.Recommended,
      RnySpotUnsold: r.src.RnySpotUnsold, RnyForwardUnsold: r.src.RnyForwardUnsold,
      EstDepletion: r.DepletionMonth, Recommendation: r.RecText,
      NotEnoughInventory: r.Short ? 'Yes' : '', ShowInPrint: r.print ? 'Yes' : 'No', Accepted: r.accepted ? 'Yes' : 'No'
    }));
    downloadCsv(rows, `bean-logic-${this.Header.CompanyID}-${new Date().toISOString().substring(0, 10)}.csv`);
  }

  // ---------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------

  get TotalRecommended(): number {
    return this.Rows.filter(r => !r.src.IsHidden).reduce((t, r) => t + r.Recommended, 0);
  }

  get ShortCount(): number {
    return this.Rows.filter(r => !r.src.IsHidden && r.Short).length;
  }

  barColor(b: BarCell, burn: number): string {
    if (b.value < 0) return 'neg';
    if (burn > 0 && b.value < burn) return 'low';
    return 'ok';
  }

  d(v: any): string | null { return dateOnly(v); }
  coverThrough(r: BeanRow): string | null { return dateOnly((r as any).coverThrough ?? r.src.CoverThrough); }
  totalRoastery(r: BeanRow): number { return Math.max(0,Number(r.roastery)||0)+r.Skus.reduce((n,s)=>n+Math.max(0,Number(s.roastery)||0),0); }
  m(v: any): string { return monthLabel(v); }
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function fmt(v: number): string {
  return Math.round(Number(v) || 0).toLocaleString('en-US');
}

/** burn override: empty = no override; 0 is a real override (customer stopped buying) */
function normBurn(v: any): number | null {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return null;
  return Math.round(Number(v) * 100) / 100;
}

/** roastery bags: empty and 0 both mean "nothing at the roastery" (the proc stores 0 as NULL) */
function normBags(v: any): number | null {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return null;
  const n = Math.round(Number(v));
  return n === 0 ? null : n;
}

/** keep a customer name safe for a file name */
function safeFileName(v: string): string {
  return String(v ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 80);
}

/** '2027-06-30…' → "Jun '27" */
function shortMonth(v: any): string {
  return monthLabel(v) || '—';
}
